import { getCurrentNickname, setCurrentNickname } from './nicknames';

const clamp01 = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return 1;
  return Math.max(0, Math.min(1, n));
};

export type MenuAction = 'test1' | 'testCtf' | 'online' | 'lobby' | 'settings' | 'profile';
export type SettingsChange = {
  music?: boolean;
  sound?: boolean;
  lang?: 'ru' | 'en';
  /** Громкость музыки 0..1 (мастер-громкость, отдельный от тогла) */
  musicVolume?: number;
  /** Громкость звуков 0..1 (мастер-громкость, отдельный от тогла) */
  soundVolume?: number;
};

export interface MenuState {
  music: boolean;
  sound: boolean;
  lang: 'ru' | 'en';
  /** 0..1, по умолчанию 1.0 */
  musicVolume?: number;
  /** 0..1, по умолчанию 1.0 */
  soundVolume?: number;
}

const i18n = {
  ru: {
    title: 'GARDEN OF DOOM',
    subtitle: 'Мультиплеерный PvP платформер',
    test1: '🥒 Тест 1 — Перекрёсток',
    testCtf: '🚩 Тест CTF — Огород',
    online: '🌐 Онлайн тест',
    lobby: '🌐 Лобби',
    settings: '⚙️ Настройки',
    profile: '👤 Профиль',
    profileTitle: 'Профиль',
    nickname: 'Никнейм',
    nicknamePlaceholder: 'Введите никнейм...',
    save: 'Сохранить',
    leaderboard: 'Таблица лидеров',
    rank: '#',
    player: 'Игрок',
    yourRank: 'Ваше место:',
    notInTop: 'Вы не попали в топ-10',
    pvp1x1: 'PvP 1×1',
    pvp3x3: 'PvP 3×3',
    pvp5x5: 'PvP 5×5',
    ctf3x3: 'CTF 3×3',
    ctf5x5: 'CTF 5×5',
    settingsTitle: 'Настройки',
    music: 'Музыка',
    sound: 'Звук',
    lang: 'Язык',
    on: 'Вкл',
    off: 'Выкл',
    back: '← Назад',
    lobbyTitle: 'Лобби',
    join: 'Присоединиться',
    searching: 'в поиске',
    players: 'игроков',
    comingSoon: 'Скоро...',
    howToPlay: '❓ Как играть',
    howToPlayTitle: 'Как играть',
    htpShoot: 'Левый клик — выстрел',
    htpReload: 'Правый клик — перезарядка',
    htpJump: 'Пробел / W — прыжок и двойной прыжок',
    htpWall: 'Персонаж прилипает к стене — прыжок откидывает в противоположную сторону',
    htpSpikes: '⚠️ Остерегайся шипов!',
    htpAmmo: 'Магазин 5 патронов — следи за цифрой над персонажем',
    htpFlag: 'CTF: захвати флаг противника и принеси на свою базу',
    htpScore: 'PvP: зарабатывай очки убийствами',
    dmTitle: 'Deathmatch',
    ctfTitle: 'Capture the Flag',
  },
  en: {
    title: 'GARDEN OF DOOM',
    subtitle: 'Multiplayer PvP Platformer',
    test1: '🥒 Test 1 — Crossroads',
    testCtf: '🚩 CTF Test — Garden',
    online: '🌐 Online Test',
    lobby: '🌐 Lobby',
    settings: '⚙️ Settings',
    profile: '👤 Profile',
    profileTitle: 'Profile',
    nickname: 'Nickname',
    nicknamePlaceholder: 'Enter nickname...',
    save: 'Save',
    leaderboard: 'Leaderboard',
    rank: '#',
    player: 'Player',
    wins: 'Wins',
    kd: 'K/D',
    yourRank: 'Your rank:',
    notInTop: 'Not in top 10',
    pvp1x1: 'PvP 1×1',
    pvp3x3: 'PvP 3×3',
    pvp5x5: 'PvP 5×5',
    ctf3x3: 'CTF 3×3',
    ctf5x5: 'CTF 5×5',
    settingsTitle: 'Settings',
    music: 'Music',
    sound: 'Sound',
    lang: 'Language',
    on: 'On',
    off: 'Off',
    back: '← Back',
    lobbyTitle: 'Lobby',
    join: 'Join',
    searching: 'searching',
    players: 'players',
    comingSoon: 'Coming soon...',
    howToPlay: '❓ How to Play',
    howToPlayTitle: 'How to Play',
    htpShoot: 'Left click — shoot',
    htpReload: 'Right click — reload',
    htpJump: 'Space / W — jump and double jump',
    htpWall: 'Character sticks to walls — jump to push off in the opposite direction',
    htpSpikes: '⚠️ Beware of spikes!',
    htpAmmo: 'Magazine holds 5 bullets — watch the number above your character',
    htpFlag: 'CTF: grab the enemy flag and bring it to your base',
    htpScore: 'PvP: score points by getting kills',
    dmTitle: 'Deathmatch',
    ctfTitle: 'Capture the Flag',
  }
};

export class MainMenu {
  private clickSound: HTMLAudioElement;
  private el: HTMLDivElement;
  private state: MenuState = (() => {
    const fallback: MenuState = { music: true, sound: true, lang: 'en', musicVolume: 1, soundVolume: 1 };
    try {
      const saved = localStorage.getItem('gameSettings');
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<MenuState>;
        return {
          ...fallback,
          ...parsed,
          // Защищаем диапазон 0..1 на случай мусора в localStorage.
          musicVolume: clamp01(parsed.musicVolume ?? 1),
          soundVolume: clamp01(parsed.soundVolume ?? 1),
        };
      }
    } catch {}
    return fallback;
  })();
  private screen: 'main' | 'settings' | 'lobby' | 'profile' | 'howtoplay' = 'main';
  private lbMode = 'pvp1x1';
  private onAction: (action: MenuAction) => void;
  private onSettings: (change: SettingsChange) => void;
  private assets: { mainBgUrl: string; clickUrl: string };

  constructor(container: HTMLElement, assets: { mainBgUrl: string; clickUrl: string }, onAction: (action: MenuAction) => void, onSettings: (change: SettingsChange) => void = () => {}) {
    this.assets = assets;
    this.onAction = onAction;
    this.onSettings = onSettings;
    this.clickSound = new Audio(assets.clickUrl);
    this.clickSound.preload = 'none';
    this.clickSound.volume = 0.7 * (this.state.soundVolume ?? 1);
    this.el = document.createElement('div');
    this.el.id = 'main-menu';
    container.appendChild(this.el);
    this.applyStyles();
    this.render();
  }

  private playClick(): void {
    if (!this.state.sound) return;
    this.clickSound.currentTime = 0;
    this.clickSound.play().catch(() => {});
  }

  private t(key: keyof typeof i18n['ru']): string {
    return i18n[this.state.lang][key];
  }

  private applyStyles(): void {
    const style = document.createElement('style');
    const mainBg = JSON.stringify(this.assets.mainBgUrl);
    style.textContent = `
      #main-menu {
        position: absolute;
        inset: 0;
        background: url(${mainBg}) center/cover no-repeat;
        background-color: #1a0a0a;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        font-family: Arial, sans-serif;
        color: #fff;
        z-index: 100;
        user-select: none;
      }
      #main-menu::before {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.55);
        z-index: 0;
      }
      #main-menu > * {
        position: relative;
        z-index: 1;
      }
      #main-menu .title {
        font-size: 52px;
        font-weight: 900;
        color: #cc2222;
        text-shadow: 0 0 30px #cc222288, 3px 3px 0 #000;
        letter-spacing: 4px;
        margin-bottom: 8px;
      }
      #main-menu .subtitle {
        font-size: 16px;
        color: #888;
        margin-bottom: 48px;
        letter-spacing: 2px;
      }
      #main-menu .screen-title {
        font-size: 28px;
        font-weight: bold;
        color: #cc2222;
        margin-bottom: 32px;
        letter-spacing: 2px;
      }
      #main-menu .btn {
        display: block;
        width: 320px;
        padding: 16px 24px;
        margin: 10px 0;
        background: rgba(10, 10, 10, 0.6);
        border: 2px solid rgba(80, 80, 80, 0.6);
        border-radius: 8px;
        color: #fff;
        font-size: 18px;
        font-weight: bold;
        cursor: pointer;
        text-align: center;
        transition: all 0.15s;
        letter-spacing: 1px;
      }
      #main-menu .btn:hover {
        background: rgba(40, 10, 10, 0.7);
        border-color: #cc2222;
        color: #ff4444;
        transform: scale(1.03);
      }
      #main-menu .btn.primary {
        background: rgba(10, 10, 10, 0.6);
        border-color: #ff4444;
      }
      #main-menu .btn.primary:hover {
        background: #ff2222;
      }
      #main-menu .btn.secondary {
        background: rgba(10, 30, 10, 0.6);
        border-color: rgba(60, 100, 60, 0.7);
      }
      #main-menu .btn.secondary:hover {
        border-color: #4a9f4a;
        color: #4a9f4a;
      }
      #main-menu .btn.back {
        background: transparent;
        border-color: #444;
        color: #888;
        width: 160px;
        padding: 10px;
        font-size: 14px;
        margin-top: 24px;
      }
      #main-menu .btn.back:hover {
        border-color: #888;
        color: #fff;
      }
      #main-menu .toggle-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        width: 420px;
        padding: 14px 20px;
        margin: 8px 0;
        background: rgba(10, 10, 10, 0.6);
        border: 2px solid rgba(80, 80, 80, 0.6);
        border-radius: 8px;
        font-size: 16px;
      }
      #main-menu .toggle-btn {
        padding: 6px 16px;
        border-radius: 6px;
        border: none;
        cursor: pointer;
        font-size: 14px;
        font-weight: bold;
        transition: all 0.15s;
      }
      #main-menu .toggle-btn.on { background: #4a9f4a; color: #fff; }
      #main-menu .toggle-btn.off { background: #3a3a3a; color: #888; }
      #main-menu .vol-row {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        margin-left: 16px;
      }
      #main-menu .vol-slider {
        flex: 1;
        appearance: none;
        height: 4px;
        background: #3a3a3a;
        border-radius: 2px;
        outline: none;
        cursor: pointer;
      }
      #main-menu .vol-slider::-webkit-slider-thumb {
        appearance: none;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: #cc2222;
        cursor: pointer;
        border: 2px solid #1a1a1a;
      }
      #main-menu .vol-slider::-moz-range-thumb {
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: #cc2222;
        cursor: pointer;
        border: 2px solid #1a1a1a;
      }
      #main-menu .vol-value {
        min-width: 36px;
        text-align: right;
        font-size: 12px;
        color: #aaa;
        font-variant-numeric: tabular-nums;
      }
      #main-menu .lang-row {
        display: flex;
        gap: 8px;
      }
      #main-menu .lang-btn {
        padding: 6px 16px;
        border-radius: 6px;
        border: 2px solid #3a3a3a;
        cursor: pointer;
        font-size: 14px;
        font-weight: bold;
        background: #1a1a1a;
        color: #888;
        transition: all 0.15s;
      }
      #main-menu .lang-btn.active {
        border-color: #cc2222;
        color: #cc2222;
        background: #2a1a1a;
      }
      #main-menu .lobby-container {
        display: flex;
        gap: 24px;
        margin-bottom: 16px;
      }
      #main-menu .lobby-group {
        background: rgba(10,10,10,0.6);
        border: 2px solid #3a3a3a;
        border-radius: 10px;
        padding: 16px 20px;
        min-width: 220px;
      }
      #main-menu .lobby-group-title {
        font-size: 14px;
        font-weight: bold;
        color: #cc2222;
        margin-bottom: 12px;
        letter-spacing: 1px;
        text-transform: uppercase;
      }
      #main-menu .lobby-row {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 10px;
      }
      #main-menu .lobby-mode-label {
        width: 40px;
        font-size: 15px;
        font-weight: bold;
        color: #fff;
      }
      #main-menu .lobby-join-btn {
        padding: 6px 14px;
        background: rgba(40,10,10,0.7);
        border: 2px solid #cc2222;
        border-radius: 6px;
        color: #ff4444;
        font-size: 13px;
        font-weight: bold;
        cursor: pointer;
        transition: all 0.15s;
      }
      #main-menu .lobby-join-btn:hover { background: rgba(80,20,20,0.8); }
      #main-menu .lobby-searching {
        font-size: 12px;
        color: #666;
      }
      #main-menu .htp-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 500px;
        margin-bottom: 20px;
      }
      #main-menu .htp-row {
        display: flex;
        align-items: flex-start;
        gap: 14px;
        background: rgba(10,10,10,0.6);
        border: 1px solid #3a3a3a;
        border-radius: 8px;
        padding: 10px 16px;
      }
      #main-menu .htp-icon {
        font-size: 20px;
        min-width: 28px;
        text-align: center;
      }
      #main-menu .htp-text {
        font-size: 14px;
        color: #ccc;
        line-height: 1.5;
      }
      #main-menu .coming-soon {
        color: #555;
        font-size: 13px;
        margin-top: 8px;
        font-style: italic;
      }
      #main-menu .profile-nick-row {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 500px;
        margin-bottom: 16px;
        font-size: 15px;
      }
      #main-menu .nick-input {
        flex: 1;
        padding: 8px 12px;
        background: rgba(10,10,10,0.7);
        border: 2px solid #3a3a3a;
        border-radius: 6px;
        color: #fff;
        font-size: 15px;
        outline: none;
      }
      #main-menu .nick-input:focus { border-color: #cc2222; }
      #main-menu .btn-save {
        padding: 8px 16px;
        background: rgba(40,10,10,0.7);
        border: 2px solid #cc2222;
        border-radius: 6px;
        color: #ff4444;
        cursor: pointer;
        font-weight: bold;
        font-size: 14px;
        transition: all 0.15s;
      }
      #main-menu .btn-save:hover { background: rgba(80,20,20,0.8); }
      #main-menu .nick-error { color: #ff4444; font-size: 13px; margin-top: -10px; margin-bottom: 10px; min-height: 18px; }
      #main-menu .lb-title {
        font-size: 18px;
        font-weight: bold;
        color: #aaa;
        margin-bottom: 10px;
        letter-spacing: 1px;
      }
      #main-menu .lb-tabs {
        display: flex;
        gap: 6px;
        margin-bottom: 10px;
        flex-wrap: wrap;
        justify-content: center;
        width: 500px;
      }
      #main-menu .lb-tab {
        padding: 6px 12px;
        background: rgba(10,10,10,0.6);
        border: 2px solid #3a3a3a;
        border-radius: 6px;
        color: #888;
        cursor: pointer;
        font-size: 13px;
        font-weight: bold;
        transition: all 0.15s;
      }
      #main-menu .lb-tab.active, #main-menu .lb-tab:hover {
        border-color: #cc2222;
        color: #ff4444;
      }
      #main-menu .lb-wrapper {
        width: 500px;
        max-height: 280px;
        overflow-y: auto;
        border: 2px solid #3a3a3a;
        border-radius: 8px;
        background: rgba(10,10,10,0.6);
        margin-bottom: 8px;
      }
      #main-menu .lb-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 14px;
      }
      #main-menu .lb-table th {
        padding: 8px 12px;
        background: rgba(30,10,10,0.8);
        color: #cc2222;
        text-align: left;
        border-bottom: 1px solid #3a3a3a;
        position: sticky;
        top: 0;
      }
      #main-menu .lb-table td {
        padding: 7px 12px;
        border-bottom: 1px solid #222;
        color: #ccc;
      }
      #main-menu .lb-table tr.highlight td { color: #ffcc00; font-weight: bold; }
      #main-menu .lb-table td.separator { text-align: center; color: #555; }
      #main-menu .divider {
        width: 320px;
        height: 1px;
        background: #2a2a2a;
        margin: 16px 0;
      }
    `;
    document.head.appendChild(style);
  }

  private render(): void {
    this.el.innerHTML = '';
    if (this.screen === 'main') this.renderMain();
    else if (this.screen === 'settings') this.renderSettings();
    else if (this.screen === 'lobby') this.renderLobby();
    else if (this.screen === 'profile') this.renderProfile();
    else if (this.screen === 'howtoplay') this.renderHowToPlay();
  }

  private renderMain(): void {
    // Демо-сборка: оставляем только Online Test, How to Play, Settings.
    // test1 / testCtf / lobby / profile скрыты на этой ветке.
    this.el.innerHTML = `
      <div class="title">${this.t('title')}</div>
      <div class="subtitle">${this.t('subtitle')}</div>
      <button class="btn secondary" id="btn-online">${this.t('online')}</button>
      <div class="divider"></div>
      <button class="btn" id="btn-howtoplay">${this.t('howToPlay')}</button>
      <button class="btn" id="btn-settings">${this.t('settings')}</button>
    `;
    this.el.querySelector('#btn-online')!.addEventListener('click', () => { this.playClick(); this.onAction('online'); });
    this.el.querySelector('#btn-howtoplay')!.addEventListener('click', () => { this.playClick(); this.screen = 'howtoplay'; this.render(); });
    this.el.querySelector('#btn-settings')!.addEventListener('click', () => { this.playClick(); this.screen = 'settings'; this.render(); });
  }

  private renderProfile(): void {
    const modes = ['pvp1x1','pvp3x3','pvp5x5','ctf3x3','ctf5x5'];
    const modeLabels: Record<string,string> = {
      pvp1x1: this.t('pvp1x1'), pvp3x3: this.t('pvp3x3'), pvp5x5: this.t('pvp5x5'),
      ctf3x3: this.t('ctf3x3'), ctf5x5: this.t('ctf5x5')
    };

    // Заглушка данных лидерборда
    const fakeData: Record<string, {name:string,wins:number,kd:number}[]> = {};
    modes.forEach(m => {
      fakeData[m] = Array.from({length:10},(_,i)=>({
        name: `Player${i+1}`,
        wins: Math.max(1, 100 - i*9 + Math.floor(Math.random()*3)),
        kd: Math.max(0.1, +(3.5 - i*0.3 + (Math.random()*0.2 - 0.1)).toFixed(2))
      })).sort((a,b) => b.wins - a.wins || b.kd - a.kd);
    });

    const lb = fakeData[this.lbMode];
    const playerNick = getCurrentNickname();
    const playerRank = lb.findIndex(p => p.name === playerNick);

    const tabsHtml = modes.map(m =>
      `<button class="lb-tab${m===this.lbMode?' active':''}" data-mode="${m}">${modeLabels[m]}</button>`
    ).join('');

    const rowsHtml = lb.map((p,i) =>
      `<tr class="${p.name===playerNick?'highlight':''}"><td>${i+1}</td><td>${p.name}</td><td>${p.wins}</td><td>${p.kd}</td></tr>`
    ).join('');

    const myRankHtml = playerRank === -1
      ? `<tr class="highlight"><td>123</td><td>${playerNick}</td><td>—</td><td>—</td></tr>`
      : '';

    this.el.innerHTML = `
      <div class="screen-title">${this.t('profileTitle')}</div>
      <div class="profile-nick-row">
        <span>${this.t('nickname')}:</span>
        <input class="nick-input" id="nick-input" type="text" value="${playerNick}" placeholder="${this.t('nicknamePlaceholder')}" maxlength="20" autocomplete="off" />
        <button class="btn-save" id="btn-save-nick">${this.t('save')}</button>
      </div>
      <div class="nick-error" id="nick-error"></div>
      <div class="lb-title">${this.t('leaderboard')}</div>
      <div class="lb-tabs">${tabsHtml}</div>
      <div class="lb-wrapper">
        <table class="lb-table">
          <thead><tr><th>${this.t('rank')}</th><th>${this.t('player')}</th><th>Wins</th><th>K/D</th></tr></thead>
          <tbody>${rowsHtml}${myRankHtml ? '<tr><td colspan="3" class="separator">···</td></tr>' + myRankHtml : ''}</tbody>
        </table>
      </div>
      <button class="btn back" id="btn-back">${this.t('back')}</button>
    `;

    const nickInput = this.el.querySelector('#nick-input') as HTMLInputElement;
    const nickError = this.el.querySelector('#nick-error') as HTMLDivElement;

    // запрещаем вводить не латинские символы прямо при наборе
    nickInput.addEventListener('input', () => {
      const val = nickInput.value;
      const filtered = val.replace(/[^a-zA-Z0-9]/g, '');
      if (val !== filtered) {
        nickInput.value = filtered;
        nickError.textContent = this.state.lang === 'ru'
          ? 'Только латинские буквы (a-z) и цифры (0-9)'
          : 'Latin letters (a-z) and digits (0-9) only';
      } else {
        nickError.textContent = '';
      }
    });

    this.el.querySelector('#btn-save-nick')!.addEventListener('click', () => {
      this.playClick();
      const val = nickInput.value.trim();
      if (!val) {
        nickError.textContent = this.state.lang === 'ru' ? 'Никнейм не может быть пустым' : 'Nickname cannot be empty';
        return;
      }
      if (!/^[a-zA-Z0-9]+$/.test(val)) {
        nickError.textContent = this.state.lang === 'ru'
          ? 'Только латинские буквы (a-z) и цифры (0-9)'
          : 'Latin letters (a-z) and digits (0-9) only';
        return;
      }
      setCurrentNickname(val);
      nickError.textContent = '';
      this.render();
    });

    modes.forEach(m => {
      this.el.querySelector(`[data-mode="${m}"]`)!.addEventListener('click', () => {
        this.playClick();
        this.lbMode = m;
        this.render();
      });
    });

    this.el.querySelector('#btn-back')!.addEventListener('click', () => { this.playClick(); this.screen = 'main'; this.render(); });
  }

  private renderHowToPlay(): void {
    const items = [
      { icon: '🖱️', text: this.t('htpShoot') },
      { icon: '🖱️', text: this.t('htpReload') },
      { icon: '⬆️', text: this.t('htpJump') },
      { icon: '🧱', text: this.t('htpWall') },
      { icon: '⚠️', text: this.t('htpSpikes') },
      { icon: '🔫', text: this.t('htpAmmo') },
      { icon: '🚩', text: this.t('htpFlag') },
      { icon: '💀', text: this.t('htpScore') },
    ];
    const rows = items.map(i => `
      <div class="htp-row">
        <span class="htp-icon">${i.icon}</span>
        <span class="htp-text">${i.text}</span>
      </div>
    `).join('');

    this.el.innerHTML = `
      <div class="screen-title">${this.t('howToPlayTitle')}</div>
      <div class="htp-list">${rows}</div>
      <button class="btn back" id="btn-back">${this.t('back')}</button>
    `;
    this.el.querySelector('#btn-back')!.addEventListener('click', () => { this.playClick(); this.screen = 'main'; this.render(); });
  }

  private renderSettings(): void {
    const musicVol = Math.round((this.state.musicVolume ?? 1) * 100);
    const soundVol = Math.round((this.state.soundVolume ?? 1) * 100);
    this.el.innerHTML = `
      <div class="screen-title">${this.t('settingsTitle')}</div>
      <div class="toggle-row">
        <span>${this.t('music')}</span>
        <div class="vol-row">
          <input type="range" class="vol-slider" id="vol-music" min="0" max="100" step="1" value="${musicVol}" />
          <span class="vol-value" id="vol-music-value">${musicVol}%</span>
        </div>
        <button class="toggle-btn ${this.state.music ? 'on' : 'off'}" id="toggle-music">
          ${this.state.music ? this.t('on') : this.t('off')}
        </button>
      </div>
      <div class="toggle-row">
        <span>${this.t('sound')}</span>
        <div class="vol-row">
          <input type="range" class="vol-slider" id="vol-sound" min="0" max="100" step="1" value="${soundVol}" />
          <span class="vol-value" id="vol-sound-value">${soundVol}%</span>
        </div>
        <button class="toggle-btn ${this.state.sound ? 'on' : 'off'}" id="toggle-sound">
          ${this.state.sound ? this.t('on') : this.t('off')}
        </button>
      </div>
      <div class="toggle-row">
        <span>${this.t('lang')}</span>
        <div class="lang-row">
          <button class="lang-btn ${this.state.lang === 'ru' ? 'active' : ''}" id="lang-ru">RU</button>
          <button class="lang-btn ${this.state.lang === 'en' ? 'active' : ''}" id="lang-en">EN</button>
        </div>
      </div>
      <button class="btn back" id="btn-back">${this.t('back')}</button>
    `;
    this.el.querySelector('#toggle-music')!.addEventListener('click', () => { this.playClick();
      this.state.music = !this.state.music; this.onSettings({ music: this.state.music }); localStorage.setItem('gameSettings', JSON.stringify(this.state)); this.render();
    });
    this.el.querySelector('#toggle-sound')!.addEventListener('click', () => { this.playClick();
      this.state.sound = !this.state.sound; this.onSettings({ sound: this.state.sound }); localStorage.setItem('gameSettings', JSON.stringify(this.state)); this.render();
    });
    // Громкости. Используем 'input' (а не 'change') чтобы реагировать живьём.
    // Не вызываем render() — только обновляем подпись %, иначе слайдер потеряет фокус
    // и DOM пересоберётся посреди драга.
    const musicSlider = this.el.querySelector('#vol-music') as HTMLInputElement;
    const musicLabel = this.el.querySelector('#vol-music-value') as HTMLSpanElement;
    musicSlider.addEventListener('input', () => {
      const v = clamp01(Number(musicSlider.value) / 100);
      this.state.musicVolume = v;
      musicLabel.textContent = `${Math.round(v * 100)}%`;
      this.onSettings({ musicVolume: v });
      localStorage.setItem('gameSettings', JSON.stringify(this.state));
    });
    const soundSlider = this.el.querySelector('#vol-sound') as HTMLInputElement;
    const soundLabel = this.el.querySelector('#vol-sound-value') as HTMLSpanElement;
    soundSlider.addEventListener('input', () => {
      const v = clamp01(Number(soundSlider.value) / 100);
      this.state.soundVolume = v;
      soundLabel.textContent = `${Math.round(v * 100)}%`;
      // Прокидываем в click-звук меню сразу же
      this.clickSound.volume = 0.7 * v;
      this.onSettings({ soundVolume: v });
      localStorage.setItem('gameSettings', JSON.stringify(this.state));
    });
    this.el.querySelector('#lang-ru')!.addEventListener('click', () => { this.playClick();
      this.state.lang = 'ru'; localStorage.setItem('gameSettings', JSON.stringify(this.state)); this.onSettings({ lang: 'ru' }); this.render();
    });
    this.el.querySelector('#lang-en')!.addEventListener('click', () => { this.playClick();
      this.state.lang = 'en'; localStorage.setItem('gameSettings', JSON.stringify(this.state)); this.onSettings({ lang: 'en' }); this.render();
    });
    this.el.querySelector('#btn-back')!.addEventListener('click', () => { this.playClick(); this.screen = 'main'; this.render(); });
  }

  private renderLobby(): void {
    const modes = [
      { id: 'dm1x1',  label: '1×1', group: 'DM',  players: 3 },
      { id: 'dm3x3',  label: '3×3', group: 'DM',  players: 7 },
      { id: 'dm5x5',  label: '5×5', group: 'DM',  players: 1 },
      { id: 'ctf3x3', label: '3×3', group: 'CTF', players: 5 },
      { id: 'ctf5x5', label: '5×5', group: 'CTF', players: 2 },
    ];

    const dmModes = modes.filter(m => m.group === 'DM');
    const ctfModes = modes.filter(m => m.group === 'CTF');

    const renderGroup = (title: string, items: typeof modes) =>
      `<div class="lobby-group">
        <div class="lobby-group-title">${title}</div>
        ${items.map(m => `
          <div class="lobby-row">
            <span class="lobby-mode-label">${m.label}</span>
            <button class="lobby-join-btn" data-mode="${m.id}">${this.t('join')}</button>
            <span class="lobby-searching">🔍 ${m.players} ${this.t('searching')}</span>
          </div>
        `).join('')}
      </div>`;

    this.el.innerHTML = `
      <div class="screen-title">${this.t('lobbyTitle')}</div>
      <div class="lobby-container">
        ${renderGroup('Deathmatch', dmModes)}
        ${renderGroup('Capture the Flag', ctfModes)}
      </div>
      <div class="coming-soon">${this.t('comingSoon')}</div>
      <button class="btn back" id="btn-back">${this.t('back')}</button>
    `;

    modes.forEach(m => {
      this.el.querySelector(`[data-mode="${m.id}"]`)!.addEventListener('click', () => {
        this.playClick();
        // TODO: подключение к серверу
      });
    });

    this.el.querySelector('#btn-back')!.addEventListener('click', () => { this.playClick(); this.screen = 'main'; this.render(); });
  }

  show(): void { this.el.style.display = 'flex'; }
  hide(): void { this.el.style.display = 'none'; }

  destroy(): void { this.el.remove(); }
}
